// One bounded, server-event-driven strike. No timers or media loops at rest.
const activeAnimations = new Set();
const activeEffects = new Set();
let generation = 0;

export function cancelArenaCombatMotion() {
    generation += 1;
    for (const animation of activeAnimations) animation.cancel();
    for (const node of activeEffects) node.remove();
    activeAnimations.clear();
    activeEffects.clear();
}

export function strikeGeometry(a, t, stage) {
    return { x: a.left - stage.left, y: a.top - stage.top,
        dx: t.left + t.width / 2 - a.left - a.width / 2,
        dy: t.top + t.height / 2 - a.top - a.height / 2,
        tx: t.left + t.width / 2 - stage.left, ty: t.top + t.height / 2 - stage.top };
}

export function isLethalArenaHit(event) {
    return event?.type === 'card_attack' && event.target_hp_after != null && Number(event.target_hp_after) <= 0;
}

async function animate(node, frames, options) {
    if (!node?.animate) return;
    const animation = node.animate(frames, { ...options, fill: 'none' });
    activeAnimations.add(animation);
    // A WebView/background suspension must never keep the action locked.
    let timeout;
    try {
        await Promise.race([animation.finished.catch(() => {}), new Promise(resolve => {
            timeout = setTimeout(() => { animation.cancel(); resolve(); }, Number(options.duration || 0) + 120);
        })]);
    } finally { clearTimeout(timeout); activeAnimations.delete(animation); animation.cancel(); }
}

function effect(stage, className, x, y) {
    const node = stage.ownerDocument.createElement('span');
    node.className = className;
    node.setAttribute('aria-hidden', 'true');
    node.style.left = `${x}px`; node.style.top = `${y}px`;
    stage.appendChild(node); activeEffects.add(node);
    return node;
}

export async function playArenaCombatEvents({ events, stage, findUnit, minimal = false, feedback = () => {}, onAttack = () => {}, onImpact = () => {} }) {
    if (!stage || stage.ownerDocument.hidden) return;
    const ticket = generation;
    const attacks = (Array.isArray(events) ? events : []).filter(event => ['card_attack','attack'].includes(event?.type)).slice(0, 2).map(event => ({
        ...event, type: 'card_attack',
        actor_battle_card_id: event.actor_battle_card_id || event.attacker_id || event.actor_card_id,
        target_battle_card_id: event.target_battle_card_id || event.target_id || event.target_card_id,
        target_hp_after: event.target_hp_after ?? event.hp_after,
        damage: event.damage ?? event.amount ?? 0,
    }));
    for (const event of attacks) {
        if (ticket !== generation || !stage.isConnected || stage.ownerDocument.hidden) break;
        const attacker = findUnit(event.actor_battle_card_id || event.actor_card_id);
        const target = findUnit(event.target_battle_card_id || event.target_card_id);
        if (!attacker || !target) continue;
        onAttack(event);
        const a = attacker.getBoundingClientRect(), t = target.getBoundingClientRect();
        const g = strikeGeometry(a, t, stage.getBoundingClientRect());
        const nodes = [];
        const clean = () => nodes.forEach(node => { node.remove(); activeEffects.delete(node); });
        try {
            const damage = effect(stage, 'arena-hit-number', g.tx, g.ty - t.height * .3);
            damage.textContent = `−${Number(event.damage || 0).toLocaleString('ru-RU')}`;
            nodes.push(damage);
            if (minimal || !attacker.animate) {
                onImpact(event);
                await animate(damage, [{ opacity: 1 }, { opacity: 1 }], { duration: 180 });
            } else {
                const ghost = effect(stage, 'arena-strike-ghost', g.x, g.y);
                ghost.style.width = `${a.width}px`; ghost.style.height = `${a.height}px`;
                // Copy only the cached still artwork, never a button/video/ID.
                const original = attacker.querySelector('img');
                const decoded = attacker.querySelector('canvas,video');
                if (original && original.naturalWidth !== 0) { const art = original.cloneNode(false); art.removeAttribute('id'); art.removeAttribute('onerror'); art.removeAttribute('onload'); ghost.appendChild(art); }
                else if (decoded) {
                    try {
                        const art = stage.ownerDocument.createElement('canvas');
                        art.width = decoded.videoWidth || decoded.width; art.height = decoded.videoHeight || decoded.height;
                        art.getContext('2d').drawImage(decoded, 0, 0); ghost.appendChild(art);
                    } catch (_) {}
                }
                nodes.push(ghost);
                damage.style.opacity = '0';
                attacker.classList.add('arena-strike-source');
                await animate(ghost, [
                    { transform: 'translate(0,0) scale(1)', offset: 0 },
                    { transform: `translate(${-g.dx * .09}px,${-g.dy * .09}px) scale(1.14) rotate(3deg)`, offset: .4 },
                    { transform: `translate(${g.dx}px,${g.dy}px) scale(1.08) rotate(-5deg)`, offset: 1 },
                ], { duration: 300, easing: 'cubic-bezier(.5,0,.85,.4)' });
                if (ticket !== generation) break;
                feedback(event.turn === 'enemy' ? 'medium' : 'heavy');
                onImpact(event);
                const burst = effect(stage, 'arena-impact-burst', g.tx, g.ty);
                burst.innerHTML = '<i></i><i></i><i></i><i></i><i></i><i></i>';
                nodes.push(burst);
                damage.style.opacity = '1';
                await Promise.all([
                    animate(ghost, [{ transform: `translate(${g.dx}px,${g.dy}px) scale(1.08)`, opacity: .9 }, { transform: 'translate(0,0) scale(1)', opacity: 0 }], { duration: 260, easing: 'cubic-bezier(.16,1,.3,1)' }),
                    animate(target, [{ transform: 'translateX(0)' }, { transform: 'translateX(5px)', offset: .25 }, { transform: 'translateX(0)' }], { duration: 180 }),
                    animate(damage, [{ transform: 'translate(-50%,0)', opacity: 1 }, { transform: 'translate(-50%,-24px)', opacity: 0 }], { duration: 320, easing: 'ease-out' }),
                ]);
            }
            if (isLethalArenaHit(event) && ticket === generation) {
                if (!minimal) {
                    const shards = effect(stage, 'arena-death-shards', g.tx, g.ty);
                    shards.innerHTML = '<i></i><i></i><i></i><i></i><i></i><i></i>';
                    nodes.push(shards);
                    await animate(target, [{ opacity: 1, transform: 'scale(1)' }, { opacity: .18, transform: 'translateY(8px) scale(.9)' }], { duration: 180, easing: 'ease-out' });
                }
                target.classList.add('dead');
                target.disabled = true;
            }
        } finally { attacker.classList.remove('arena-strike-source'); clean(); }
    }
}
